import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LIBRARY_TRANSLATIONS, flattenTranslations } from '@card-software/library';

@Injectable({
  providedIn: 'root',
})

/* This code defines an Angular code that is used to load translations from a JSON() 
file and dynamically extract translations based on the selected language. It is part of the integration with the module */
export class CustomTranslateLoader implements TranslateLoader {
  private http = inject(HttpClient);

  private readonly translationsUrl = '/assets/i18n/translations.json';

  getTranslation(lang: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, unknown>>(this.translationsUrl).pipe(
      // App translations win over the library's own (filters panel, nav menu…) when a key exists in both.
      map((translations) => ({
        ...(flattenTranslations(LIBRARY_TRANSLATIONS, lang) as Record<string, string>),
        ...this.flattenTranslations(translations, lang),
      })),
      catchError((error) => {
        console.error('Translation load error:', error);
        return of({}); // Return empty object on error
      }),
    );
  }

  private flattenTranslations(node: Record<string, unknown>, lang: string, path = ''): Record<string, string> {
    let result: Record<string, string> = {};

    Object.keys(node).forEach((key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const value = node[key] as Record<string, unknown>;

      if (value && typeof value === 'object' && value[lang] === undefined) {
        // Nested node (e.g., User_details_tabs)
        result = {
          ...result,
          ...this.flattenTranslations(value, lang, currentPath),
        };
      } else if (value[lang]) {
        // Translation leaf node (e.g., Roles)
        result[currentPath] = value[lang] as string;
      } else {
        console.warn(`Missing translation for "${currentPath}" in ${lang}`);
        result[currentPath] = currentPath; // Fallback to path
      }
    });

    return result;
  }
}
